// src/components/dashboards/SegmentationWidget.jsx
import React, { useState, useEffect } from 'react';
import { get } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { FaUsers, FaChartBar, FaClock, FaShoppingCart, FaGift } from 'react-icons/fa';

const SegmentationWidget = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedPeriod, setSelectedPeriod] = useState('12');

    useEffect(() => {
        fetchSegmentationDashboard();
    }, [selectedPeriod]);

    const fetchSegmentationDashboard = async () => {
        setLoading(true);
        try {
            // Intentar obtener datos del dashboard de segmentación mejorado
            const data = await get(`clientes/analytics/segmentation-dashboard?period=${selectedPeriod}`);
            setDashboardData(data);
        } catch (error) {
            console.error('Error fetching segmentation dashboard:', error);
            // Fallback a endpoint básico
            try {
                const basicData = await get('clientes/segments');
                setDashboardData({
                    topRfmSegments: basicData,
                    summary: { totalCustomers: basicData.reduce((sum, s) => sum + s.count, 0) }
                });
            } catch (fallbackError) {
                console.error('Error fetching basic segments:', fallbackError);
                Swal.fire('Error', fallbackError.message, 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const getSegmentColor = (segmentName) => {
        const colors = {
            'Champions': 'bg-green-100 text-green-800',
            'Loyal Customers': 'bg-blue-100 text-blue-800',
            'At Risk': 'bg-yellow-100 text-yellow-800',
            'Can\'t Lose': 'bg-red-100 text-red-800',
            'About to Sleep': 'bg-orange-100 text-orange-800',
            'Lost': 'bg-gray-100 text-gray-800',
            'Fin de semana': 'bg-purple-100 text-purple-800',
            'Nocturnos': 'bg-indigo-100 text-indigo-800',
            'Cazapromos': 'bg-pink-100 text-pink-800',
            'Ticket alto': 'bg-emerald-100 text-emerald-800'
        };
        return colors[segmentName] || 'bg-gray-100 text-gray-800';
    };

    if (loading) {
        return (
            <div className="p-4">
                <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
                    <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded"></div>
                        <div className="h-3 bg-gray-200 rounded"></div>
                        <div className="h-3 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 bg-white rounded-lg shadow border">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold flex items-center">
                    <FaChartBar className="mr-2 text-blue-500" />
                    Segmentación Avanzada
                </h3>
                <select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1"
                >
                    <option value="3">3m</option>
                    <option value="6">6m</option>
                    <option value="12">12m</option>
                    <option value="24">24m</option>
                </select>
            </div>

            {/* Resumen de métricas */}
            {dashboardData?.summary && (
                <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="bg-blue-50 p-2 rounded text-center">
                        <div className="flex items-center justify-center mb-1">
                            <FaUsers className="text-blue-500 text-sm mr-1" />
                            <span className="text-xs font-medium">Total</span>
                        </div>
                        <div className="text-lg font-bold text-blue-600">
                            {dashboardData.summary.totalCustomers}
                        </div>
                    </div>
                    <div className="bg-green-50 p-2 rounded text-center">
                        <div className="flex items-center justify-center mb-1">
                            <FaShoppingCart className="text-green-500 text-sm mr-1" />
                            <span className="text-xs font-medium">Ticket</span>
                        </div>
                        <div className="text-lg font-bold text-green-600">
                            ${dashboardData.summary.avgBasketSize?.toFixed(0) || '0'}
                        </div>
                    </div>
                </div>
            )}

            {/* Segmentos RFM principales */}
            <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Segmentos RFM</h4>
                <div className="space-y-2">
                    {dashboardData?.topRfmSegments?.slice(0, 3).map(segment => (
                        <div key={segment.name || segment.id} className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">
                                {segment.name || segment.label}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs ${getSegmentColor(segment.name || segment.label)}`}>
                                {segment.count || segment.customers?.length || 0}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Segmentos de comportamiento principales */}
            {dashboardData?.topAdditionalSegments && dashboardData.topAdditionalSegments.length > 0 && (
                <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Comportamiento</h4>
                    <div className="space-y-2">
                        {dashboardData.topAdditionalSegments.slice(0, 3).map(segment => (
                            <div key={segment.name} className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">
                                    {segment.name}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs ${getSegmentColor(segment.name)}`}>
                                    {segment.count}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Métricas adicionales si están disponibles */}
            {dashboardData?.summary && (
                <div className="border-t pt-2 mt-2">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center">
                            <FaClock className="text-orange-500 mr-1" />
                            <span className="text-gray-600">Freq:</span>
                            <span className="font-semibold ml-1">
                                {dashboardData.summary.avgFrequency?.toFixed(1) || '0'}
                            </span>
                        </div>
                        <div className="flex items-center">
                            <FaGift className="text-pink-500 mr-1" />
                            <span className="text-gray-600">Promos:</span>
                            <span className="font-semibold ml-1">
                                {((dashboardData.summary.avgPromoUsage || 0) * 100).toFixed(0)}%
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {(!dashboardData || (!dashboardData.topRfmSegments && !dashboardData.topAdditionalSegments)) && (
                <p className="text-gray-500 text-sm">No hay datos de segmentación disponibles</p>
            )}
        </div>
    );
};

export default SegmentationWidget;
